import {
  Creative,
  CreativeDocumentContent,
  CreativeDocumentElement,
  CreativeDocumentMedium,
  Point,
  Rectangle,
  Transform,
} from 'content-creation-common/models';
import { isAudible, isVisible } from 'content-creation-common/utils/content';
import { getElementTransformAtFrame } from 'content-creation-common/utils/transform';
import { deepCopy } from 'content-creation-common/utils/object';

import {
  CONTENT_TYPE_LABEL,
  CONTENT_TYPE,
  FIT_MODES,
  MEDIA_TYPE,
  PLAYBACK_FRAMES_PER_SECOND,
  SHAPE_PROPERTIES,
  TEXT_EFFECTS,
} from '../constants';
import { GROUP_ROLES } from '../features/group/constants';
import {
  DEFAULT_KEYFRAME_NUMBER,
  DEFAULT_SHAPE_COLOR,
} from '../features/creative/constants';
import { DEFAULT_TEXT_FILL } from '../features/text/constants';
import { getKeyFramesFromTransformsMap } from '../util/keyframe';
import { isEmpty as isEmptyArray } from '../util/array';
import {
  convertMapToObject,
  deepEqual,
  isEmpty as isEmptyObject,
  isEmptyString,
  isNil,
  isNotNil,
  mapToMap,
} from '../util/object';
import { getMediumSpaceTransform } from '../util/transform';
import { isNotNumber } from '../util/numbers';
import { captureDebug } from '../util/sentry';
import { isGroup } from '../features/group/util';
import { hasAudio } from '../features/media/util';
import { isChangedFromTemplate as isLayerChangedFromTemplate } from '../features/shared/layer/util';

import Medium from './Medium';
import Template from './Template';
import Content from './Content';

import { DEFAULT_ALPHA_BLEND_MODE } from '../constants/AlphaBlendModes';
import { TIME_RANGE_ABSOLUTE_MAX_FRAME } from '../features/shared/creative/state-management/constants';

/**
 * Layer
 * Holds properties related to layers (aka elements) in a creative.
 */
export default class Layer {
  _blendMode = DEFAULT_ALPHA_BLEND_MODE;
  _content;
  _description;
  _editedTransform;
  _endFrame = TIME_RANGE_ABSOLUTE_MAX_FRAME;
  _groupId; // TODO use parentLayer?
  _groupRole;
  _id;
  _isEditingTransform = false;
  _isTransformedByUser = false;
  _label = 'Layer'; // name
  _locked;
  _meta;
  _originalLayer;
  _parentContentId; // TODO use parentLayer? (also, include child layers?)
  _selectable = true;
  _selected = false;
  _startFrame = 0;
  _transforms;

  /**
   * @param {Content} content
   * @param {String|null} id
   * @param {Layer} originalLayer
   * @param {boolean=} selected
   */
  constructor(content, id = null, originalLayer = null, selected = false) {
    if (!Content.isContent(content)) {
      throw new Error('Layer must have LayerContent');
    }

    if (isNotNil(originalLayer) && !Layer.isLayer(originalLayer)) {
      throw new Error('originalLayer must be of type Layer');
    }

    this._content = content;

    this._id = isEmptyString(id) ? CreativeDocumentElement.getId() : id;
    this._originalLayer = originalLayer;
    this._selected = selected;

    this._transforms = new Map();
    this._transforms.set(
      DEFAULT_KEYFRAME_NUMBER,
      new Transform(DEFAULT_KEYFRAME_NUMBER),
    );

    this._locked = {};
    this._meta = {};
    this._label = CONTENT_TYPE_LABEL[content.contentType];
  }

  /*
    Convenience Methods
   */

  addTransform(keyframe, transform) {
    this.transforms.set(keyframe, transform);
  }

  removeTransform(keyframe) {
    if (this.transforms.has(keyframe)) {
      this.transforms.delete(keyframe);
    }
  }

  equals(inLayer) {
    if (!Layer.isLayer(inLayer)) return false;

    const propertyEquality =
      this.blendMode === inLayer.blendMode &&
      this.description === inLayer.description &&
      this.endFrame === inLayer.endFrame &&
      this.groupId === inLayer.groupId &&
      this.groupRole === inLayer.groupRole &&
      this.id === inLayer.id &&
      this.isEditingTransform === inLayer.isEditingTransform &&
      this.isTransformedByUser === inLayer.isTransformedByUser &&
      this.label === inLayer.label &&
      this.parentContentId === inLayer.parentContentId &&
      this.selectable === inLayer.selectable &&
      // this.selected === inLayer.selected &&
      this.startFrame === inLayer.startFrame &&
      deepEqual(this.editedTransform, inLayer.editedTransform) &&
      deepEqual(this.locked, inLayer.locked) &&
      deepEqual(this.meta, inLayer.meta) &&
      deepEqual(this.transforms, inLayer.transforms);

    if (!propertyEquality) return false;

    const contentEquality = this.content.equals(inLayer.content);
    if (!contentEquality) return false;

    if (Layer.isLayer(this.originalLayer)) {
      const originalLayerEquality = this.originalLayer.equals(
        inLayer.originalLayer,
      );
      if (!originalLayerEquality) return false;
    } else if (Layer.isLayer(inLayer.originalLayer)) {
      return false;
    }

    return true;
  }

  /*
    Getters and Setters for Element-Like properties
   */

  get blendMode() {
    return this._blendMode;
  }

  set blendMode(value) {
    this._blendMode = value;
  }

  get content() {
    return this._content;
  }

  set content(value) {
    if (!Content.isContent(value)) {
      throw new Error('Layer must have LayerContent');
    }

    this._content = value;
  }

  get description() {
    return this._description;
  }

  set description(value) {
    this._description = value;
  }

  get editedTransform() {
    return this._editedTransform;
  }

  set editedTransform(value) {
    this._editedTransform = value;
  }

  get endFrame() {
    return this._endFrame;
  }

  set endFrame(value) {
    this._endFrame = value;
  }

  get groupId() {
    return this._groupId;
  }

  set groupId(value) {
    this._groupId = value;
  }

  get groupRole() {
    return this._groupRole;
  }

  set groupRole(value) {
    this._groupRole = value;
  }

  get id() {
    return this._id;
  }

  set id(value) {
    this._id = value;
  }

  get isEditingTransform() {
    return this._isEditingTransform;
  }

  set isEditingTransform(value) {
    this._isEditingTransform = value;
  }

  get isTransformedByUser() {
    return this._isTransformedByUser;
  }

  set isTransformedByUser(value) {
    this._isTransformedByUser = value;
  }

  get label() {
    return this._label;
  }

  set label(value) {
    this._label = value;
  }

  get locked() {
    return this._locked;
  }

  set locked(value) {
    this._locked = value;
  }

  get meta() {
    return this._meta;
  }

  set meta(value) {
    this._meta = value;
  }

  get originalLayer() {
    return this._originalLayer;
  }

  set originalLayer(value) {
    this._originalLayer = value;
  }

  get parentContentId() {
    return this._parentContentId;
  }

  set parentContentId(value) {
    this._parentContentId = value;
  }

  get selectable() {
    return this._selectable;
  }

  set selectable(value) {
    this._selectable = value;
  }

  get selected() {
    return this._selected;
  }

  set selected(value) {
    this._selected = value;
  }

  get startFrame() {
    return this._startFrame;
  }

  set startFrame(value) {
    this._startFrame = value;
  }

  get transforms() {
    return this._transforms;
  }

  set transforms(value) {
    this._transforms = value;
  }

  /*
    Convenience Getters
   */

  get audible() {
    return isAudible(this.content) && this.hasAudio;
  }

  get autoTextHeight() {
    if (!Layer.isTextLayer(this)) return 0;
    return (
      this.textNaturalHeight + (2 * this.textFontAdjustment + this.textPadding)
    );
  }

  get canAutoExpand() {
    return Layer.isTextLayer(this) && this.height < this.autoTextHeight;
  }

  get creativeFieldId() {
    return this.content.creativeFieldId;
  }

  /** @returns {boolean} */
  // NOTE the difference between this convenience method `excluded` vs the property on meta `exclude`
  get excludedFromTemplate() {
    return isNotNil(this.originalLayer)
      ? this.originalLayer.excludedFromTemplate
      : this.meta.excludeFromTemplate;
  }

  /** @returns {Object|Undefined} */
  get bounds() {
    if (isNil(this.meta.bounds)) return undefined;
    const {
      bounds: { centerX, centerY, height, width },
    } = this.meta;
    return new Rectangle(centerX, centerY, width, height);
  }

  /** @returns {number} */
  get centerX() {
    return this.hasMedium ? this.medium.centerX : undefined;
  }

  // TODO(lemieux) remove usages of this in favor of id
  get elementId() {
    return this.id;
  }

  /** @returns {!Object} */
  get medium() {
    return this.content.medium;
  }

  get loaded() {
    return this.hasMedium ? this.medium.loaded : false;
  }

  /** @returns {string} */
  get contentId() {
    return this.content.contentId;
  }

  /** @returns {string} */
  get contentType() {
    return this.content.contentType;
  }

  /** @returns {string} */
  get fitMode() {
    return this.meta.fitMode;
  }

  /** @returns {number} */
  get endTime() {
    return this.endFrame / PLAYBACK_FRAMES_PER_SECOND;
  }

  get hasAudio() {
    return (
      this.hasMedium &&
      hasAudio(
        this.mediumType,
        this.medium.audioServerId,
        this.medium.processedAudio,
      )
    );
  }

  get hasMedium() {
    return this.content.hasMedium;
  }

  get isAllTransformsLocked() {
    const lockedValues = Object.values(this.locked);
    return (
      lockedValues.length > 0 &&
      lockedValues.every((i) => {
        return i;
      })
    );
  }

  /** @returns {string} */
  get mediumId() {
    return this.hasMedium ? this.medium.mediaId : undefined;
  }

  /** @returns {string} */
  get mediumType() {
    return this.hasMedium ? this.medium.mediaType : undefined;
  }

  /** @returns {string} */
  get mediumServerId() {
    return this.hasMedium ? this.medium.serverId : undefined;
  }

  /** @returns {string} */
  get mediumUrl() {
    return this.hasMedium ? this.medium.URL : undefined;
  }

  /** @returns {number} */
  get startTime() {
    return this.startFrame / PLAYBACK_FRAMES_PER_SECOND;
  }

  /** @returns {!Array<!Object>} */
  get keyFrames() {
    return getKeyFramesFromTransformsMap(this.transforms);
  }

  /** This represents the position of the layer irrespective of keyframe animation. */
  get canvasSpaceTransform() {
    // This is using the first keyframe to be consistent, but any keyframe would have been fine,
    // but all the layers need to use the same one!
    const element = Layer.getCreativeDocumentElement(this);
    return getElementTransformAtFrame(
      element.toJSON(),
      this.keyFrames[0].frame,
    );
  }

  get mediumSpaceTransformation() {
    const originalLayer = !Layer.isDisplayLayer(this)
      ? this.originalLayer.canvasSpaceTransform
      : undefined;
    return getMediumSpaceTransform(originalLayer, this.canvasSpaceTransform);
  }

  get height() {
    return this.hasMedium ? this.medium.height : undefined;
  }

  get width() {
    return this.hasMedium ? this.medium.width : undefined;
  }

  get fillColor() {
    return this.content.fillColor || '#000000';
  }

  get textEffect() {
    return this.content.textEffect || undefined;
  }

  get textEffectColor() {
    if (isNil(this.textEffect)) return undefined;
    if (
      isNil(this.textEffect.type) ||
      this.textEffect.type === TEXT_EFFECTS.NONE
    ) {
      return undefined;
    }
    return this.textEffect.color || undefined;
  }

  get textString() {
    return this.content.textString;
  }

  get textFillMode() {
    return !isEmptyObject(this.content.textFill) &&
      isNotNil(this.content.textFill.mode)
      ? this.content.textFill.mode
      : DEFAULT_TEXT_FILL;
  }

  get textFontFamily() {
    return this.content.font;
  }

  get textFontSize() {
    return this.content.fontSize;
  }

  get textFontAdjustment() {
    return this.hasMedium ? this.medium.fontAdjustment : 0;
  }

  get textNaturalHeight() {
    return this.content.naturalHeight || 0;
  }

  get textNaturalWidth() {
    return this.content.naturalWidth || 0;
  }

  get textPadding() {
    return this.content.padding || 0;
  }

  get uploaded() {
    return this.hasMedium && this.medium.uploaded;
  }

  get visible() {
    return isVisible(this.content);
  }

  clone() {
    return Layer.cloneLayer(this);
  }

  copy() {
    return Layer.copyLayer(this);
  }

  /*
    Static Methods
   */

  /**
   * Returns a de-referenced copy of Layer
   * @return {Layer}
   */
  static copyLayer(layer) {
    if (!Layer.isLayer(layer)) {
      throw new Error('Must provide a Layer');
    }

    // Create new content
    const creativeContent = Layer.getCreativeDocumentContent(layer);

    // Create new media, can be null.
    let creativeMedium = null;

    // Create new media object but not for groups
    if (isNotNil(layer.medium)) {
      creativeMedium = Layer.getCreativeDocumentMedium(layer);
    }

    const creativeElement = Layer.getCreativeDocumentElement(layer);

    const copiedLayer = Layer.getLayerFromCreativeDocumentObjects(
      creativeContent,
      creativeElement,
      creativeMedium,
    );

    copiedLayer.originalLayer = layer.originalLayer;
    return copiedLayer;
  }

  /**
   * Similar to copyLayer, however, elementId, contentId, and mediaId will be new
   * in addition, content element ids will be empty which means that group will lose its children
   * @return {Layer}
   */
  static cloneLayer(layer) {
    if (!Layer.isLayer(layer)) {
      throw new Error('Must provide a Layer');
    }

    const clonedLayer = Layer.copyLayer(layer);
    const content = clonedLayer.content;
    const media = clonedLayer.medium;

    // update element id
    clonedLayer.id = CreativeDocumentElement.getId();

    // update content id
    content.contentId = CreativeDocumentContent.getId();

    // cloning a layer will not also clone its children, thus,
    // we remove children if content is an element group here
    const isGroupContentType = Layer.isGroupLayer(clonedLayer);
    if (isGroupContentType) {
      clonedLayer.content.contentElementIds = [];
    }

    // Create new media id but not for groups
    if (isNotNil(media)) {
      media.mediaId = CreativeDocumentMedium.getId();
    }

    return clonedLayer;
  }

  /**
   * Assigns parent/child references to layers.
   * @param {Layer} parentLayer
   * @param {Layer} childLayer
   * @param {number|null} index
   */
  static addChildLayer = (parentLayer, childLayer, index = null) => {
    if (!Layer.isGroupLayer(parentLayer)) {
      throw new Error('parentLayer must be of type Layer');
    }

    if (!Layer.isLayer(childLayer)) {
      throw new Error('childLayer must be of type Layer');
    }

    const { contentElementIds } = parentLayer.content;
    // if index is provide, add child at index or else add to the end of the list
    const insertIndex = isNotNil(contentElementIds[index])
      ? index
      : contentElementIds.length;
    contentElementIds.splice(insertIndex, 0, childLayer.id);

    // Tie child layer back to group layer
    childLayer.groupId = parentLayer.id;
    childLayer.parentContentId = parentLayer.contentId;
  };

  /**
   * Remove parent/child references to layers.
   * @param {Layer} parentLayer
   * @param {Layer} childLayer
   */
  static removeChildLayer = (parentLayer, childLayer) => {
    if (!Layer.isGroupLayer(parentLayer)) {
      throw new Error('parentLayer must be a Group Layer');
    }

    if (!Layer.isLayer(childLayer)) {
      throw new Error('childLayer must be of type Layer');
    }

    const { contentElementIds } = parentLayer.content;

    const foundIndex = contentElementIds.indexOf(childLayer.id);
    if (foundIndex === -1) {
      throw new Error('childLayer is not a child of parentLayer');
    }

    contentElementIds.splice(foundIndex, 1);

    // Remove parent from child layer
    childLayer.groupId = undefined;
    childLayer.parentContentId = undefined;
  };

  /**
   *
   * @param {!Layer|undefined} layer
   * @param {!Template|undefined} template
   * @returns {undefined|!Rectangle}
   */
  // @todo(chuti): refactor to not rely on template
  static generateBoundingBoxInMediumSpace = (layer, template) => {
    if (!Layer.isLayer(layer) || !Template.isTemplate(template)) {
      return undefined;
    }

    const { id: elementId, endFrame, medium, startFrame } = layer;
    if (
      !Medium.isMedium(medium) ||
      isNotNumber(medium.width) ||
      isNotNumber(medium.height)
    ) {
      return undefined;
    }

    const { rootFrameEnd, templateHeight, templateWidth } = template;
    const mediumRectangle = new Rectangle(0, 0, medium.width, medium.height);
    let intersectionPoints = [];

    // check intersection of all frames
    const lastFrame = endFrame > rootFrameEnd ? rootFrameEnd : endFrame;
    for (let frame = startFrame; frame <= lastFrame; frame++) {
      const canvasRelativeTransformOfLayerAtFrame = template.getCanvasRelativeElementTransformAtFrame(
        elementId,
        frame,
      );

      if (isNil(canvasRelativeTransformOfLayerAtFrame)) {
        captureDebug('generateBoundingBoxInMediumSpace - no transforms', {
          elementId,
        });
        continue;
      }

      const {
        rotation,
        scale: { x: scaleX, y: scaleY },
        translation: { x, y },
      } = canvasRelativeTransformOfLayerAtFrame;

      // check for valid transformations
      if (
        isNotNumber(rotation) ||
        isNotNumber(scaleX) ||
        isNotNumber(scaleY) ||
        isNotNumber(x) ||
        isNotNumber(y)
      ) {
        captureDebug('generateBoundingBoxInMediumSpace - invalid transforms', {
          elementId,
          canvasRelativeTransformOfLayerAtFrame,
        });
        continue;
      }

      const center = new Point(-x / scaleX, -y / scaleY);
      center.rotateAroundPoint(new Point(), -rotation);

      const canvasRectangle = new Rectangle(
        center.x,
        center.y,
        templateWidth / scaleX,
        templateHeight / scaleY,
        -rotation,
      );

      const intersectionPolygon = mediumRectangle.getClippedWith(
        canvasRectangle,
      );
      // ignore no intersection
      if (
        isNil(intersectionPolygon) ||
        isEmptyArray(intersectionPolygon.points)
      ) {
        continue;
      } // No points to add, this iteration.

      intersectionPoints = [
        ...intersectionPoints,
        ...intersectionPolygon.points,
      ];
    }

    if (isEmptyArray(intersectionPoints)) return undefined;

    return Rectangle.getMinimumBoundingRectangleFromPoints(intersectionPoints);
  };

  /**
   * returns scale that will fit/fill layer in a given bound
   *
   * @param layer
   * @param {Rectangle} bounds
   * @returns {number|undefined} scale
   */
  static getScaleToFitLayerToBounds(layer, bounds) {
    if (!Layer.isLayer(layer) || !Layer.isLayer(layer.originalLayer)) {
      return undefined;
    }
    if (!Rectangle.isValidRectangle(bounds)) return undefined;

    const { medium, originalLayer } = layer;

    if (isNil(medium)) return undefined;

    // center point of the bound in relative to media
    // will need to add (x, y) to current keyframes to position replacement media at the center of bound
    const mediumRectangle = new Rectangle(0, 0, medium.width, medium.height);
    const { fitMode } = originalLayer;

    return fitMode === FIT_MODES.FILL
      ? mediumRectangle.getScaleToFillIn(bounds)
      : mediumRectangle.getScaleToFitIn(bounds);
  }

  /**
   * returns keyframes that will fit/fill layer in a given bound
   *
   * @param layer
   * @param {Rectangle} bounds
   * @returns {object|undefined} transforms
   */
  static getTransformsToFitLayerToBounds(layer, bounds) {
    if (!Layer.isLayer(layer) || !Layer.isLayer(layer.originalLayer)) {
      return undefined;
    }
    if (!Rectangle.isValidRectangle(bounds)) return undefined;

    const scale = Layer.getScaleToFitLayerToBounds(layer, bounds);
    if (isNotNumber(scale)) return undefined;

    const { originalLayer } = layer;
    const { transforms: originalTransforms } = originalLayer;
    const keyframeMap = mapToMap(originalTransforms, (transform) => {
      return transform.copy();
    });
    const { x, y } = bounds;
    const scaledDeltaTranslationX =
      x * originalLayer.canvasSpaceTransform.scale.x;
    const scaledDeltaTranslationY =
      y * originalLayer.canvasSpaceTransform.scale.y;

    const keyframes = {};
    keyframeMap.forEach((transform, frame) => {
      transform.translation.x += scaledDeltaTranslationX;
      transform.translation.y += scaledDeltaTranslationY;

      transform.scale.x *= scale;
      transform.scale.y *= scale;
      keyframes[frame] = transform;
    });

    return keyframes;
  }

  /**
   * Get bounds for the layer at a specific frame, irrespective of it's parent.
   * @param {Layer} layer Layer to constraint transformation for.
   * @param {Number} frameNumber to get transforms for
   */
  static getLocalBoundsForFrame(layer, frameNumber) {
    if (!Layer.isLayer(layer) || isNotNumber(frameNumber)) {
      return null;
    }
    const { transforms } = layer;
    if (isNil(transforms)) return null;

    const currentTransform = transforms.get(
      transforms.has(frameNumber) ? frameNumber : DEFAULT_KEYFRAME_NUMBER,
    );
    const { scale, translation } = currentTransform;
    return new Rectangle(
      translation.x,
      translation.y,
      layer.width * scale.x,
      layer.height * scale.y,
    );
  }

  /**
   * @function getConstrainedAbsoluteTranslationForFrame
   * @param  {Layer} layer Layer to constraint transformation for.
   * @param  {Point} newTranslation The translation we are trying to contraint.
   * @param  {Number} frameNumber Frame number we are translating.
   * @param  {Rectangle} container  Coordinates relative to the canvas we are constraining translations to.
   * @return {Point} representing constrained x and y translations within the container
   */
  static getConstrainedAbsoluteTranslationForFrame(
    layer,
    newTranslation,
    frameNumber,
    container,
  ) {
    if (
      !Layer.isLayer(layer) ||
      !Point.isPoint(newTranslation) ||
      !Rectangle.isRectangle(container) ||
      isNotNumber(frameNumber)
    ) {
      captureDebug('getConstrainedAbsoluteTranslationForFrame debug', {
        newTranslation,
        frameNumber,
      });
      return null;
    }
    const transform = layer.transforms.get(frameNumber); // Frame numbers are stored as strings
    if (isNil(transform)) {
      captureDebug('getConstrainedAbsoluteTranslationForFrame debug', {
        transforms: convertMapToObject(layer.transforms, (transform) => {
          return transform.getCreativeDocumentTransform().toJSON();
        }),
        frameNumber,
      });
      return null;
    }
    const {
      scale: { x: scaleX, y: scaleY },
    } = transform;

    const scaledMediaWidth = layer.width * scaleX;
    const scaledMediaHeight = layer.height * scaleY;

    const translationRect = new Rectangle(
      newTranslation.x,
      newTranslation.y,
      scaledMediaWidth,
      scaledMediaHeight,
    );
    return translationRect.getTranslationToFillRect(container);
  }

  /**
   * @function getConstrainedRelativeTranslationForFrame
   * @param  {Layer} layer Layer to constraint transformation for.
   * @param  {Point} relativeTranslation relative to the current translation
   * @param  {Number} frameNumber Frame number we are translating.
   * @param  {Rectangle} container  Coordinates relative to the canvas we are constraining translations to.
   * @return {Point} representing the relative transformation allowed
   */
  static getConstrainedRelativeTranslationForFrame(
    layer,
    relativeTranslation,
    frameNumber,
    container,
  ) {
    if (
      !Layer.isLayer(layer) ||
      !Point.isPoint(relativeTranslation) ||
      !Rectangle.isRectangle(container) ||
      isNotNumber(frameNumber)
    ) {
      return null;
    }
    const transform = layer.transforms.get(frameNumber); // Frame numbers are stored as strings
    if (isNil(transform)) {
      return null;
    }
    const {
      scale: { x: scaleX, y: scaleY },
      translation: { x: translationX, y: translationY },
    } = transform;

    const scaledMediaWidth = layer.width * scaleX;
    const scaledMediaHeight = layer.height * scaleY;

    const translationRect = new Rectangle(
      translationX + relativeTranslation.x,
      translationY + relativeTranslation.y,
      scaledMediaWidth,
      scaledMediaHeight,
    );
    const constrainedAbsoluteTranslation = translationRect.getTranslationToFillRect(
      container,
    );
    return new Point(
      constrainedAbsoluteTranslation.x - translationX,
      constrainedAbsoluteTranslation.y - translationY,
    );
  }

  /**
   *
   * @param {Layer} layer
   * @returns {CreativeDocumentElement}
   */
  static getCreativeDocumentElement(layer) {
    if (!Layer.isLayer(layer)) {
      throw new Error(
        'Cannot get CreativeDocumentElement, layer is not a Layer',
      );
    }

    const creativeDocumentElement = new CreativeDocumentElement(
      layer.contentId,
      layer.id,
    );
    creativeDocumentElement.alphaCompositeRule = layer.blendMode;
    creativeDocumentElement.editedTransform = layer.editedTransform;
    creativeDocumentElement.description = layer.description;
    creativeDocumentElement.frameEnd = layer.endFrame;
    creativeDocumentElement.frameStart = layer.startFrame;
    creativeDocumentElement.groupId = layer.groupId;
    creativeDocumentElement.groupRole = layer.groupRole;
    creativeDocumentElement.isEditingTransform = layer.isEditingTransform;
    creativeDocumentElement.isTransformedByUser = layer.isTransformedByUser;
    creativeDocumentElement.locked = deepCopy(layer.locked);
    creativeDocumentElement.meta = deepCopy(layer.meta);
    creativeDocumentElement.name = layer.label;
    creativeDocumentElement.parentContentId = layer.parentContentId;
    creativeDocumentElement.selectable = layer.selectable;
    creativeDocumentElement.transforms = mapToMap(layer.transforms, (value) =>
      value.getCreativeDocumentTransform(),
    );

    return creativeDocumentElement;
  }

  /**
   *
   * @param {Layer} layer
   * @returns {CreativeDocumentContent}
   */
  static getCreativeDocumentContent(layer) {
    if (!Layer.isLayer(layer)) {
      throw new Error(
        'Cannot get CreativeDocumentContent, layer is not a Layer',
      );
    }

    const content = layer.content;
    return Content.getCreativeDocumentContent(content);
  }

  /**
   *
   * @param {Layer} layer
   * @returns {CreativeDocumentMedium}
   */
  static getCreativeDocumentMedium(layer) {
    if (!Layer.isLayer(layer)) {
      throw new Error(
        'Cannot get CreativeDocumentMedium, layer is not a Layer',
      );
    }

    if (!layer.hasMedium) return null;

    const content = layer.content;
    const medium = content.medium;

    return Medium.getCreativeDocumentMedium(medium);
  }

  /**
   * @param {!Object} element
   * @param {!Object} content
   * @param {!Object} medium
   * @param {!Template=} template
   * @param {boolean=} selected
   */
  static getLayerFromReduxStateObjects(
    element,
    content,
    medium,
    template = undefined,
    selected = false,
  ) {
    const isTemplate = Template.isTemplate(template);
    const originalLayer = isTemplate
      ? template.getLayer(element.elementId)
      : undefined;

    const creativeDocumentElement = CreativeDocumentElement.fromJSON(element);
    const creativeDocumentContent = CreativeDocumentContent.fromJSON(content);
    const creativeDocumentMedium = CreativeDocumentMedium.fromJSON(medium);

    const layer = Layer.getLayerFromCreativeDocumentObjects(
      creativeDocumentContent,
      creativeDocumentElement,
      creativeDocumentMedium,
    );
    layer.originalLayer = originalLayer;
    layer.selected = selected;

    return layer;
  }

  /**
   *
   * @param {CreativeDocumentContent} creativeDocumentContent
   * @param {CreativeDocumentElement} creativeDocumentElement
   * @param {CreativeDocumentMedium} creativeDocumentMedium
   * @returns {Layer}
   */
  static getLayerFromCreativeDocumentObjects(
    creativeDocumentContent,
    creativeDocumentElement,
    creativeDocumentMedium,
  ) {
    const content = Content.fromCreativeDocumentObject(
      creativeDocumentContent,
      creativeDocumentMedium,
    );
    const layer = new Layer(content, creativeDocumentElement.elementId);

    // Apply element-specific values on to Layer
    // TODO figure out the right properties for Layer in this context.
    layer.blendMode = creativeDocumentElement.alphaCompositeRule;
    layer.description = creativeDocumentElement.description;
    layer.editedTransform = creativeDocumentElement.editedTransform;
    layer.endFrame = creativeDocumentElement.frameEnd;
    layer.startFrame = creativeDocumentElement.frameStart;
    layer.groupId = creativeDocumentElement.groupId;
    layer.groupRole = creativeDocumentElement.groupRole;
    layer.isEditingTransform = creativeDocumentElement.isEditingTransform;
    layer.isTransformedByUser = creativeDocumentElement.isTransformedByUser;
    layer.parentContentId = creativeDocumentElement.parentContentId;
    layer.selectable = creativeDocumentElement.selectable;

    const transforms = new Map();
    layer.transforms = transforms;
    if (isNotNil(creativeDocumentElement.transforms)) {
      creativeDocumentElement.transforms.forEach((value, key) => {
        const keyframe = parseInt(key, 10);
        transforms.set(
          keyframe,
          Transform.fromCreativeDocumentObject(keyframe, value),
        );
      });
    }

    if (!isNil(creativeDocumentElement.locked)) {
      layer.locked = deepCopy(creativeDocumentElement.locked);
    }

    if (!isNil(creativeDocumentElement.meta)) {
      layer.meta = deepCopy(creativeDocumentElement.meta);
    }

    if (!isEmptyString(creativeDocumentElement.name)) {
      layer.label = creativeDocumentElement.name;
    }

    return layer;
  }

  /**
   * Turn a Layer in to a CreativeLayer
   *
   * In the future we should replace Layer with CreativeLayer as Layer is becoming legacy and outliving its usefulness.
   *
   * @param {Layer} layer
   * @returns {CreativeLayer}
   */
  static getCreativeLayer(layer) {
    return Creative.getCreativeLayerFromCreativeDocument(
      Layer.getCreativeDocumentElement(layer),
      Layer.getCreativeDocumentContent(layer),
      Layer.getCreativeDocumentMedium(layer),
    );
  }

  /**
   * Turn a CreativeLayer in to a Layer
   *
   * In the future we should replace Layer with CreativeLayer as Layer is becoming legacy and outliving its usefulness.
   *
   * @param {CreativeLayer} creativeLayer
   * @returns {Layer}
   */
  static getLayerFromCreativeLayer(creativeLayer) {
    return Layer.getLayerFromCreativeDocumentObjects(
      creativeLayer.content,
      creativeLayer.element,
      creativeLayer.media,
    );
  }

  /**
   * Checks if object is an instance of Layer
   *
   * @param inputObject
   * @returns {boolean}
   */
  static isLayer(inputObject) {
    return inputObject instanceof Layer;
  }

  /**
   * Checks if layer is a text layer
   *
   * @param layer
   * @returns {boolean}
   */
  static isTextLayer(layer) {
    return Layer.isLayer(layer) && layer.contentType === CONTENT_TYPE.TEXT;
  }

  /**
   * Checks if layer is a group layer
   *
   * @param layer
   * @returns {boolean}
   */
  static isGroupLayer(layer) {
    return Layer.isLayer(layer) && isGroup(layer.contentType);
  }

  /**
   * Checks if layer is a video layer
   *
   * @param layer
   * @returns {boolean}
   */
  static isVideoLayer(layer) {
    return Layer.isLayer(layer) && layer.contentType === CONTENT_TYPE.VIDEO;
  }

  /**
   * Checks if layer is a display only layer
   *
   * @param layer
   * @returns {boolean}
   */
  static isDisplayLayer(layer) {
    return Layer.isLayer(layer) && layer.groupRole === GROUP_ROLES.DISPLAY;
  }

  /**
   * Checks if layer is a mask layer
   *
   * @param layer
   * @returns {boolean}
   */
  static isMaskLayer(layer) {
    return Layer.isLayer(layer) && layer.groupRole === GROUP_ROLES.MASK;
  }

  /**
   * Determines if provided layer has been altered to be distinct from the original layer from the template.
   * @param {Layer} layer
   * @param {Template} template
   * @returns {*}
   */
  static hasChangedFromOriginalLayer(layer, template) {
    if (!Layer.isLayer(layer)) {
      throw new Error('Cannot check status on object that is not a layer');
    }

    if (layer.excludedFromTemplate) return true;

    const { originalLayer } = layer;

    // No original layer means this one is different than null;
    if (!Layer.isLayer(originalLayer)) return true;

    const content = Layer.getCreativeDocumentContent(layer);
    const medium = Layer.getCreativeDocumentMedium(layer);

    const originalElement = Layer.getCreativeDocumentElement(
      layer.originalLayer,
    );

    return isLayerChangedFromTemplate({
      content,
      medium,
      currentTemplate: template,
      templateElement: originalElement,
    });
  }

  static createShapeLayer(shape, fillColor = DEFAULT_SHAPE_COLOR) {
    // Create a media object in the model

    const { width, height } = SHAPE_PROPERTIES[shape];

    // Create the medium
    const medium = new Medium({
      height,
      width,
      mediaType: MEDIA_TYPE.INLINE_SVG,
    });

    // Set uploaded (default for shape)
    medium.setLoaded(true);
    medium.setUploaded(true);

    // Create the content
    const content = new Content(CONTENT_TYPE.SHAPE, null, medium);
    content.shape = shape;
    content.fillColor = fillColor;

    return new Layer(content);
  }
}
